// A task group's toolchains and its own worker image (D90): read them, set presets and a setup
// script (the owner; a new script needs CustomiseWorkerImage), and forget the last build so
// the next task builds again.
import { queryOptions } from "@tanstack/react-query";

import { api, type Schemas } from "../../api/client";
import { unwrap } from "../../api/queries";
import { TOOLCHAINS, type Toolchain } from "../../catalog/types";

export type ToolchainsDto = Schemas["ToolchainsDto"];
export type GroupImage = Schemas["GroupImageDto"];
export type ImageState = "building" | "ready" | "failed";

const groupPath = (id: string) => ({ params: { path: { task_group_id: id } } });

/** The API's presets, in table order, unknown names left out. */
export const presetsOf = (dto: Pick<ToolchainsDto, "presets"> | undefined): Toolchain[] =>
  TOOLCHAINS.filter((t) => dto?.presets.includes(t));

/** While a build runs the card polls, so "building" turns to ready or failed by itself. */
const BUILDING_POLL_MS = 5_000;

export const toolchainsQuery = (groupId: string) =>
  queryOptions({
    queryKey: ["task-groups", groupId, "toolchains"],
    queryFn: () => unwrap(api.GET("/api/task-groups/{task_group_id}/toolchains", groupPath(groupId))),
    refetchInterval: (q) => (q.state.data?.image?.state === "building" ? BUILDING_POLL_MS : false),
  });

export const setToolchains = (groupId: string, presets: Toolchain[], script: string | null) =>
  unwrap(api.PUT("/api/task-groups/{task_group_id}/toolchains", { ...groupPath(groupId), body: { presets, script } }));

export const rebuildImage = (groupId: string) =>
  unwrap(api.POST("/api/task-groups/{task_group_id}/toolchains/rebuild", groupPath(groupId)));
